// Lightweight, batched, privacy-conscious visitor tracking.
// Runs only in the browser; safe no-ops during SSR.

import { ingestEvents } from "./ingest.functions";

const VISITOR_KEY = "mp_visitor_id";
const SESSION_KEY = "mp_session_id";
const SESSION_TS_KEY = "mp_session_ts";
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;

type EventPayload = {
  event_type: string;
  path?: string;
  referrer?: string;
  metadata?: Record<string, unknown>;
};

type HeatmapPayload = {
  kind: "click" | "rage_click" | "dead_click" | "scroll";
  path: string;
  x_pct?: number;
  y_pct?: number;
  viewport_w?: number;
  viewport_h?: number;
  scroll_depth?: number;
  target?: string;
};

type FunnelPayload = {
  funnel: string;
  step: string;
  step_index: number;
  metadata?: Record<string, unknown>;
};

type SessionMeta = {
  visitor_id: string;
  session_id: string;
  landing_path?: string;
  referrer?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  user_agent?: string;
  device_type?: string;
  viewport_w?: number;
  viewport_h?: number;
};

function isBrowser() {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

function uid(): string {
  try {
    if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  } catch {}
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function getVisitorId(): string {
  try {
    let id = localStorage.getItem(VISITOR_KEY);
    if (!id) {
      id = uid();
      localStorage.setItem(VISITOR_KEY, id);
    }
    return id;
  } catch {
    return "anon";
  }
}

function getSessionId(): string {
  try {
    const now = Date.now();
    const ts = Number(sessionStorage.getItem(SESSION_TS_KEY) || 0);
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id || now - ts > SESSION_TIMEOUT_MS) {
      id = uid();
      sessionStorage.setItem(SESSION_KEY, id);
    }
    sessionStorage.setItem(SESSION_TS_KEY, String(now));
    return id;
  } catch {
    return "anon";
  }
}

function detectDevice(): string {
  if (!isBrowser()) return "unknown";
  const w = window.innerWidth;
  if (w < 640) return "mobile";
  if (w < 1024) return "tablet";
  return "desktop";
}

function readUTM(search: string): Partial<SessionMeta> {
  const out: Partial<SessionMeta> = {};
  try {
    const p = new URLSearchParams(search);
    for (const k of ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"] as const) {
      const v = p.get(k);
      if (v) (out as any)[k] = v.slice(0, 100);
    }
  } catch {}
  return out;
}

// --- Batching ---
type Queue = {
  events: EventPayload[];
  heatmap: HeatmapPayload[];
  funnel: FunnelPayload[];
};
let queue: Queue = { events: [], heatmap: [], funnel: [] };
let flushTimer: number | null = null;
let sessionStarted = false;
let lastClickTime = 0;
let clickCluster = { x: 0, y: 0, count: 0, t: 0 };

function scheduleFlush() {
  if (!isBrowser()) return;
  if (flushTimer != null) return;
  flushTimer = window.setTimeout(() => {
    flushTimer = null;
    void flush();
  }, 4000);
}

async function flush() {
  if (!isBrowser()) return;
  if (!queue.events.length && !queue.heatmap.length && !queue.funnel.length) return;
  const payload = {
    visitor_id: getVisitorId(),
    session_id: getSessionId(),
    events: queue.events,
    heatmap: queue.heatmap,
    funnel: queue.funnel,
  };
  queue = { events: [], heatmap: [], funnel: [] };
  try {
    await ingestEvents({ data: payload });
  } catch (err) {
    // swallow — never break the public site for analytics
    if (process.env.NODE_ENV === "development") console.warn("[track] flush failed", err);
  }
}

function flushSync() {
  if (!isBrowser()) return;
  // sendBeacon for unload
  if (!queue.events.length && !queue.heatmap.length && !queue.funnel.length) return;
  try {
    const payload = JSON.stringify({
      visitor_id: getVisitorId(),
      session_id: getSessionId(),
      ...queue,
    });
    const blob = new Blob([payload], { type: "application/json" });
    navigator.sendBeacon?.("/_serverFn/ingestEvents", blob);
  } catch {}
  queue = { events: [], heatmap: [], funnel: [] };
}

function ensureSession() {
  if (!isBrowser() || sessionStarted) return;
  sessionStarted = true;
  const meta: SessionMeta = {
    visitor_id: getVisitorId(),
    session_id: getSessionId(),
    landing_path: window.location.pathname,
    referrer: document.referrer || undefined,
    user_agent: navigator.userAgent,
    device_type: detectDevice(),
    viewport_w: window.innerWidth,
    viewport_h: window.innerHeight,
    ...readUTM(window.location.search),
  };
  queue.events.push({
    event_type: "session_start",
    path: window.location.pathname,
    referrer: document.referrer || undefined,
    metadata: meta as unknown as Record<string, unknown>,
  });
  scheduleFlush();

  // Lifecycle hooks
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushSync();
  });
  window.addEventListener("pagehide", () => flushSync());

  // Click tracking (heatmap + rage clicks)
  document.addEventListener(
    "click",
    (e) => {
      try {
        const t = e.target as HTMLElement | null;
        const target = t ? describeTarget(t) : undefined;
        const x_pct = (e.clientX / window.innerWidth) * 100;
        const y_pct = (e.clientY / window.innerHeight) * 100;
        queue.heatmap.push({
          kind: "click",
          path: window.location.pathname,
          x_pct,
          y_pct,
          viewport_w: window.innerWidth,
          viewport_h: window.innerHeight,
          target,
        });

        // Rage click detection: 3+ clicks within 800ms in ~50px radius
        const now = Date.now();
        if (now - clickCluster.t < 800 && Math.hypot(e.clientX - clickCluster.x, e.clientY - clickCluster.y) < 50) {
          clickCluster.count += 1;
          if (clickCluster.count >= 3) {
            queue.heatmap.push({
              kind: "rage_click",
              path: window.location.pathname,
              x_pct,
              y_pct,
              target,
            });
            clickCluster = { x: 0, y: 0, count: 0, t: 0 };
          }
        } else {
          clickCluster = { x: e.clientX, y: e.clientY, count: 1, t: now };
        }

        // CTA tracking
        const cta = t?.closest("[data-cta]") as HTMLElement | null;
        if (cta) {
          queue.events.push({
            event_type: "cta_click",
            path: window.location.pathname,
            metadata: { cta: cta.dataset.cta?.slice(0, 80), target },
          });
        }
        lastClickTime = now;
        scheduleFlush();
      } catch {}
    },
    { capture: true, passive: true },
  );

  // Scroll depth
  let maxDepth = 0;
  let scrollTimer: number | null = null;
  window.addEventListener(
    "scroll",
    () => {
      if (scrollTimer != null) return;
      scrollTimer = window.setTimeout(() => {
        scrollTimer = null;
        const h = document.documentElement;
        const depth = Math.min(100, Math.round(((window.scrollY + window.innerHeight) / h.scrollHeight) * 100));
        if (depth > maxDepth + 9) {
          maxDepth = depth;
          queue.heatmap.push({
            kind: "scroll",
            path: window.location.pathname,
            scroll_depth: depth,
            viewport_w: window.innerWidth,
            viewport_h: window.innerHeight,
          });
          scheduleFlush();
        }
      }, 250);
    },
    { passive: true },
  );
}

function describeTarget(el: HTMLElement): string {
  const tag = el.tagName.toLowerCase();
  const id = el.id ? `#${el.id}` : "";
  const cls = typeof el.className === "string" ? el.className.split(/\s+/).slice(0, 2).join(".") : "";
  const text = (el.textContent || "").trim().slice(0, 40);
  return `${tag}${id}${cls ? "." + cls : ""}${text ? `:${text}` : ""}`.slice(0, 120);
}

// --- Public API ---
export const track = {
  init() {
    ensureSession();
  },
  pageview(path?: string) {
    if (!isBrowser()) return;
    ensureSession();
    queue.events.push({
      event_type: "pageview",
      path: path ?? window.location.pathname,
      referrer: document.referrer || undefined,
    });
    scheduleFlush();
  },
  event(event_type: string, metadata?: Record<string, unknown>) {
    if (!isBrowser()) return;
    ensureSession();
    queue.events.push({
      event_type,
      path: window.location.pathname,
      metadata,
    });
    scheduleFlush();
  },
  funnel(funnel: string, step: string, step_index: number, metadata?: Record<string, unknown>) {
    if (!isBrowser()) return;
    ensureSession();
    queue.funnel.push({ funnel, step, step_index, metadata });
    scheduleFlush();
  },
};

export default track;